// MarketingApp — root for the public, no-auth marketing surface.
// Mounted by main.jsx when the pathname is not /demo and not /app*.
//
// Path dispatch is pathname-based (clean URLs, not hashes) to match
// the standalone marketing site at seidrplatform.com. Adding a new
// marketing page = add a route line below and a new file under
// frontend/src/marketing/pages/.
//
// Auth0Provider IS mounted here even though marketing pages don't
// require auth — the Nav's "Sign in" button calls loginWithRedirect()
// to bounce to Auth0, and after callback the user lands on /app
// (the redirect_uri the provider is configured with in App.jsx).

import React, { useEffect, useState } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { OrgProvider, useOrg } from '../contexts/OrgContext';
import { MarketingLangProvider } from './i18n.jsx';
import HomePage from './pages/HomePage';
import BookPage from './pages/BookPage';
import './marketing.css';

// Same Auth0 config the product side uses — single org pilot.
const AUTH0_DOMAIN     = import.meta.env?.VITE_AUTH0_DOMAIN || '';
const AUTH0_CLIENT_ID  = import.meta.env?.VITE_AUTH0_CLIENT_ID || '';
const AUTH0_AUDIENCE   = import.meta.env?.VITE_AUTH0_AUDIENCE || '';
const AUTH0_ORG_ID     = import.meta.env?.VITE_AUTH0_ORG_ID || '';
const AUTH0_CONFIGURED = AUTH0_DOMAIN && !AUTH0_DOMAIN.includes('your-tenant');

// `redirect_uri` lands the post-Auth0 user inside the product (/app)
// regardless of whether they kicked off login from /, /pricing,
// /book, etc. Keep in sync with the same value in App.jsx.
const POST_LOGIN_REDIRECT = `${typeof window !== 'undefined' ? window.location.origin : ''}/app`;

function dispatchPage() {
  const path = (typeof window !== 'undefined' ? window.location.pathname : '/').replace(/\/$/, '') || '/';
  // /pricing was removed on 2026-10-14; old links land on the homepage.
  if (path === '/book')    return <BookPage />;
  return <HomePage />;
}

function MarketingShell() {
  // Re-dispatch on popstate (back/forward) so SPA navigation between
  // marketing pages works without full reloads. Internal links from
  // marketing → /app or /demo SHOULD trigger a full reload (those
  // cross into different root bundles).
  const [, setTick] = useState(0);
  useEffect(() => {
    const onPop = () => setTick((t) => t + 1);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return <div className="marketing-root">{dispatchPage()}</div>;
}

// Per-tenant subdomains (e.g. italia.seidr.makers.bot) can have their OWN
// Auth0 organization — see App.jsx's AuthenticatedApp for the full
// rationale. The marketing bundle's "Sign in" button must resolve the
// same org, otherwise a visitor landing on e.g. italia.seidr.makers.bot's
// homepage and clicking "Sign in" gets signed up under the wrong
// Auth0 organization (the static VITE_AUTH0_ORG_ID fallback) instead of
// italia's dedicated org — which then collides with any existing
// account under that email and Auth0 fails with a generic error.
function OrgAwareAuth0({ children }) {
  const { org, loading: orgLoading } = useOrg();

  if (orgLoading) return null;

  const organizationId = org?.id || AUTH0_ORG_ID || undefined;

  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: POST_LOGIN_REDIRECT,
        audience: AUTH0_AUDIENCE || undefined,
        organization: organizationId,
      }}
      cacheLocation="memory"
    >
      {children}
    </Auth0Provider>
  );
}

export default function MarketingApp() {
  if (AUTH0_CONFIGURED) {
    return (
      <OrgProvider>
        <OrgAwareAuth0>
          <MarketingLangProvider>
            <MarketingShell />
          </MarketingLangProvider>
        </OrgAwareAuth0>
      </OrgProvider>
    );
  }
  return (
    <OrgProvider>
      <MarketingLangProvider>
        <MarketingShell />
      </MarketingLangProvider>
    </OrgProvider>
  );
}
